import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref, type Ref } from 'lit/directives/ref.js'
import maplibregl from 'maplibre-gl'
import maplibreCssText from 'maplibre-gl/dist/maplibre-gl.css?inline'
import { vectorStyleCacheService } from '../../services/tile-cache/vector-style-cache-service.js'

@customElement('activity-track-map')
export class ActivityTrackMap extends LitElement {
  static override styles = [
    unsafeCSS(maplibreCssText),
    css`
      :host {
        display: block;
        width: 100%;
        height: 200px;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid var(--cairn-border-color, #e2e8f0);
        position: relative;
        cursor: pointer;
      }

      :host([expanded]) {
        height: auto;
        border-radius: 0;
        border: none;
        cursor: default;
      }

      .map-container {
        width: 100%;
        height: 100%;
      }

      .tap-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 5;
        cursor: pointer;
      }

      :host([expanded]) .tap-overlay {
        display: none;
      }

      .expand-hint {
        position: absolute;
        bottom: 8px;
        right: 8px;
        background: rgba(0, 0, 0, 0.5);
        color: white;
        border-radius: 4px;
        padding: 2px 6px;
        font-size: 11px;
        pointer-events: none;
        z-index: 6;
      }

      :host([expanded]) .expand-hint {
        display: none;
      }

      .track-controls {
        position: absolute;
        top: 8px;
        left: 50%;
        transform: translateX(-50%);
        display: none;
        flex-direction: row;
        z-index: 10;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        border-radius: 12px;
        overflow: hidden;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }

      :host([expanded]) .track-controls {
        display: flex;
      }

      .material-icons {
        font-family: 'Material Symbols Outlined';
        font-weight: normal;
        font-style: normal;
        font-size: 20px;
        line-height: 1;
        display: inline-block;
      }

      .track-btn {
        background: var(--cairn-surface-color, #ffffff);
        border: none;
        border-radius: 0;
        width: 40px;
        height: 40px;
        color: var(--cairn-text-secondary-color, #64748b);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.15s ease;
      }

      .track-btn:first-child {
        border-start-start-radius: 12px;
        border-end-start-radius: 12px;
      }

      .track-btn:last-child {
        border-start-end-radius: 12px;
        border-end-end-radius: 12px;
      }

      .track-btn:not(:first-child) {
        border-left: 1px solid var(--cairn-border-color, #e2e8f0);
      }

      .track-btn.active {
        background: var(--cairn-primary-color, #3b82f6);
        color: white;
      }
    `,
  ]

  @property({ type: Array }) trackCoords: GeoJSON.Position[] = []
  @property({ type: Array }) rawTrackCoords: GeoJSON.Position[] = []
  @property({ type: Array }) uncertaintyPolygon: GeoJSON.Position[] = []
  @property({ type: Array }) restPoints: [number, number][] = []
  @property({ type: Boolean, reflect: true }) expanded = false

  @state() private showNormalized = true
  @state() private showRaw = false
  @state() private showCorridor = false

  private mapRef: Ref<HTMLDivElement> = createRef()
  private map: maplibregl.Map | null = null
  private sourcesReady = false
  private resizeObserver: ResizeObserver | null = null

  override firstUpdated(): void {
    this.initMap()
    this.resizeObserver = new ResizeObserver(() => {
      this.map?.resize()
    })
    this.resizeObserver.observe(this)
  }

  override updated(changed: Map<string, unknown>): void {
    if (
      changed.has('trackCoords') ||
      changed.has('rawTrackCoords') ||
      changed.has('uncertaintyPolygon') ||
      changed.has('restPoints')
    ) {
      this.updateSources()
    }
    if (changed.has('showNormalized') || changed.has('showRaw') || changed.has('showCorridor')) {
      this.updateLayerVisibility()
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    this.map?.remove()
    this.map = null
  }

  private expand(): void {
    if (this.expanded) return
    this.expanded = true
    this.setAttribute('expanded', '')
    this.dispatchEvent(new CustomEvent('map-expand', { bubbles: true, composed: true }))
    if (this.map) {
      this.map.dragPan.enable()
      this.map.scrollZoom.enable()
      this.map.touchZoomRotate.enable()
      this.map.doubleClickZoom.enable()
      setTimeout(() => this.map?.resize(), 0)
    }
  }

  private handleMapClick(): void {
    if (!this.expanded) {
      this.expand()
    }
  }

  private async initMap(): Promise<void> {
    if (!this.mapRef.value) return

    const interactive = this.expanded
    const style = await vectorStyleCacheService.loadStyle(
      'https://tiles.openfreemap.org/styles/liberty'
    )
    this.map = new maplibregl.Map({
      container: this.mapRef.value,
      style,
      center: [0, 0],
      zoom: 1,
      attributionControl: false,
      renderWorldCopies: false,
      pixelRatio: Math.min(devicePixelRatio, 2),
      fadeDuration: 0,
      dragPan: interactive,
      scrollZoom: interactive,
      touchZoomRotate: interactive,
      doubleClickZoom: interactive,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    })

    this.map.on('load', () => {
      this.addLayers()
      this.sourcesReady = true
      this.updateSources()
      this.fitBounds()
    })
  }

  private addLayers(): void {
    if (!this.map) return

    const emptyFC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

    this.map.addSource('corridor', { type: 'geojson', data: emptyFC })
    this.map.addSource('raw-track', { type: 'geojson', data: emptyFC })
    this.map.addSource('track', { type: 'geojson', data: emptyFC })
    this.map.addSource('rests', { type: 'geojson', data: emptyFC })

    this.map.addLayer({
      id: 'corridor-fill',
      type: 'fill',
      source: 'corridor',
      paint: {
        'fill-color': '#3b82f6',
        'fill-opacity': 0.35,
      },
      layout: { visibility: 'none' },
    })

    this.map.addLayer({
      id: 'corridor-outline',
      type: 'line',
      source: 'corridor',
      paint: {
        'line-color': '#3b82f6',
        'line-width': 1.5,
        'line-opacity': 0.5,
      },
      layout: { visibility: 'none' },
    })

    this.map.addLayer({
      id: 'raw-track-line',
      type: 'line',
      source: 'raw-track',
      paint: {
        'line-color': '#ef4444',
        'line-width': 2,
        'line-opacity': 0.7,
      },
      layout: {
        visibility: 'none',
        'line-cap': 'round',
        'line-join': 'round',
      },
    })

    this.map.addLayer({
      id: 'track-line-casing',
      type: 'line',
      source: 'track',
      paint: {
        'line-color': '#ffffff',
        'line-width': 5,
      },
    })

    this.map.addLayer({
      id: 'track-line',
      type: 'line',
      source: 'track',
      paint: {
        'line-color': '#2563eb',
        'line-width': 3,
      },
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
    })

    this.map.addLayer({
      id: 'rest-points',
      type: 'circle',
      source: 'rests',
      paint: {
        'circle-radius': 5,
        'circle-color': '#f59e0b',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
      },
    })
  }

  private updateSources(): void {
    if (!this.map || !this.sourcesReady) return

    const trackGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        this.trackCoords.length >= 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: this.trackCoords },
              },
            ]
          : [],
    }

    const rawGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        this.rawTrackCoords.length >= 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'LineString', coordinates: this.rawTrackCoords },
              },
            ]
          : [],
    }

    const corridorGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        this.uncertaintyPolygon.length >= 4
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: { type: 'Polygon', coordinates: [this.uncertaintyPolygon] },
              },
            ]
          : [],
    }

    const restGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: this.restPoints.map(pt => ({
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'Point' as const, coordinates: pt },
      })),
    }

    ;(this.map.getSource('track') as maplibregl.GeoJSONSource).setData(trackGeoJSON)
    ;(this.map.getSource('raw-track') as maplibregl.GeoJSONSource).setData(rawGeoJSON)
    ;(this.map.getSource('corridor') as maplibregl.GeoJSONSource).setData(corridorGeoJSON)
    ;(this.map.getSource('rests') as maplibregl.GeoJSONSource).setData(restGeoJSON)
    this.fitBounds()
  }

  private updateLayerVisibility(): void {
    if (!this.map || !this.sourcesReady) return
    const v = (show: boolean) => (show ? 'visible' : 'none')
    this.map.setLayoutProperty('track-line', 'visibility', v(this.showNormalized))
    this.map.setLayoutProperty('track-line-casing', 'visibility', v(this.showNormalized))
    this.map.setLayoutProperty('raw-track-line', 'visibility', v(this.showRaw))
    this.map.setLayoutProperty('corridor-fill', 'visibility', v(this.showCorridor))
    this.map.setLayoutProperty('corridor-outline', 'visibility', v(this.showCorridor))
  }

  private fitBounds(): void {
    if (!this.map || this.trackCoords.length < 2) return

    const bounds = new maplibregl.LngLatBounds()
    for (const coord of this.trackCoords) {
      bounds.extend([coord[0], coord[1]])
    }

    this.map.fitBounds(bounds, { padding: 20, duration: 0 })
  }

  private toggleNormalized(): void {
    this.showNormalized = !this.showNormalized
  }

  private toggleRaw(): void {
    this.showRaw = !this.showRaw
  }

  private toggleCorridor(): void {
    this.showCorridor = !this.showCorridor
  }

  override render(): unknown {
    return html`
      <div class="map-container" ${ref(this.mapRef)}></div>
      <div class="tap-overlay" @click=${this.handleMapClick}></div>
      <span class="expand-hint">Tap to expand</span>
      <div class="track-controls">
        <button
          class="track-btn ${this.showNormalized ? 'active' : ''}"
          @click=${this.toggleNormalized}
          title="Normalized track"
        >
          <span class="material-icons">show_chart</span>
        </button>
        <button
          class="track-btn ${this.showRaw ? 'active' : ''}"
          @click=${this.toggleRaw}
          title="Raw GPS track"
        >
          <span class="material-icons">gesture</span>
        </button>
        <button
          class="track-btn ${this.showCorridor ? 'active' : ''}"
          @click=${this.toggleCorridor}
          title="Uncertainty corridor"
        >
          <span class="material-icons">blur_on</span>
        </button>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'activity-track-map': ActivityTrackMap
  }
}
